import { makeStyles } from '@material-ui/core';
import cn from 'classnames';
import { useContext, useEffect } from 'react';
import PersonalNotesAttachments from '../../components/common/PersonalNotes/personal-notes-attachments';
import PersonalNotesComments from '../../components/common/PersonalNotes/personal-notes-comments';
import { Attachment } from '../../components/common/attachment-card';
import Editor from '../../components/editor/editor';
import { usePersonalNotes } from '../../contexts/personal-notes-context';
import { UserContext } from '../../contexts/user-context';
import { COLORS } from '../../theme/variables';
import { getMentionConfigByNoteType } from '../../utils/mentions';
import NoteEditorActions from './note-editor-actions';

const useStyles = makeStyles(() => ({
  inputWrapper: {
    position: 'relative',
    zIndex: 2,
    flex: '1 1 0',
    height: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  inputContainer: {
    width: '100%',
    flex: 1,
    minHeight: 0,
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.25)',
      opacity: 0,
      transition: 'opacity 160ms ease',
      zIndex: 1,
      pointerEvents: 'none',
    },
    '&.mask::after': {
      opacity: 1,
    },
  },
  editorContainer: {
    border: 'none',
  },
  toolbar: {
    background: COLORS.COLOR_ADDITIONAL_WHITE,
  },
}));

const parseAttachments = (attachmentRefs: string): Attachment[] => {
  try {
    return JSON.parse(attachmentRefs);
  } catch (e: any) {
    return [];
  }
};

function NoteEditor() {
  const classes = useStyles();
  const { hasAccessToAction } = useContext(UserContext);
  const {
    selectedNote,
    activeBlock,
    isReadOnlySelectedNote,
    setValue,
    setAttachments,
    closeAllBlocks,
  } = usePersonalNotes();

  useEffect(() => {
    // Update note content and attachments when selected note is changed
    if (selectedNote) {
      setValue(selectedNote.content);
      const parsedAttachments = parseAttachments(
        selectedNote.attachmentRefs || '',
      );
      setAttachments(parsedAttachments || []);
    } else {
      setValue('');
      setAttachments([]);
    }
  }, [selectedNote, setAttachments, setValue]);

  return (
    <>
      <div className={classes.inputWrapper}>
        <div
          className={cn(classes.inputContainer, {
            mask: !!activeBlock,
          })}
          onClick={closeAllBlocks}>
          <Editor
            initialValue={selectedNote?.content}
            onChange={setValue}
            placeholder='Add a note'
            disabled={
              !hasAccessToAction('personalNotes.update') ||
              isReadOnlySelectedNote
            }
            key={selectedNote?.id}
            containerClassName={classes.editorContainer}
            toolbarClassName={classes.toolbar}
            hasMentions
            mentionSource={getMentionConfigByNoteType(selectedNote?.type)}
          />
        </div>
        {hasAccessToAction('personalNotes.update') && (
          <PersonalNotesAttachments />
        )}
        <PersonalNotesComments />
      </div>
      {hasAccessToAction('personalNotes.update') && <NoteEditorActions />}
    </>
  );
}

export default NoteEditor;
